import { Check, Mail, XCircle } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  isPayrollStatementDeliveryFailed,
  isTextOnlyDelivery,
} from "./delivery-status-rules";
import { SmsDeliveryStatus } from "./sms-delivery-status";
import type { PayrollStatementNotificationDelivery } from "./types";
import {
  formatNotificationTimestamp,
  getDeliveryTimestamp,
  getNotificationReasonLabel,
} from "./utils";

export const DeliveryHistory = ({
  deliveries,
}: {
  deliveries: PayrollStatementNotificationDelivery[];
}) => {
  if (deliveries.length === 0) {
    return (
      <Stack align="center" gap="sm" className="px-6 py-12 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-secondary">
          <Mail className="h-5 w-5 text-tertiary-text" />
        </div>
        <Typography variant="bodyW3" textColor="tertiary">
          Nothing sent to this technician yet.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" fullWidth className="px-6 pb-8">
      {deliveries.map((delivery, index) => {
        const isFailed = isPayrollStatementDeliveryFailed(delivery.status);
        const timestamp = formatNotificationTimestamp(
          getDeliveryTimestamp(delivery),
        );
        const key =
          delivery.id ?? delivery.delivery_id ?? `${delivery.status}-${index}`;

        return (
          <Stack key={key} direction="row" align="start" gap="md" fullWidth>
            <div
              className={cn(
                "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                {
                  "bg-red-10 text-red-50": isFailed,
                  "bg-green-10 text-green-60": !isFailed,
                },
              )}
            >
              {isFailed ? (
                <XCircle className="h-4 w-4" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </div>
            <Stack gap="xs" className="min-w-0 flex-1">
              <Typography variant="subtitleW3">
                {isFailed
                  ? "Send failed"
                  : isTextOnlyDelivery(delivery)
                    ? "PayPack texted"
                    : "PayPack emailed"}
              </Typography>
              {timestamp && (
                <Typography variant="captionW1" textColor="tertiary">
                  {timestamp}
                </Typography>
              )}
              {delivery.actor_name && (
                <Typography variant="captionW1" textColor="tertiary">
                  by {delivery.actor_name}
                </Typography>
              )}
              {delivery.note && (
                <Typography
                  variant="bodyW3"
                  textColor="secondary"
                  className="mt-1 whitespace-pre-line border-l-2 border-stroke-tertiary pl-2"
                >
                  {delivery.note}
                </Typography>
              )}
              {delivery.error && (
                <Typography variant="captionW1" textColor="negative">
                  {getNotificationReasonLabel(delivery.error) ?? delivery.error}
                </Typography>
              )}
              {delivery.sms && <SmsDeliveryStatus sms={delivery.sms} />}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
